import { ReactNode } from "react";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";

import { ProgramFormTab, programFormTabOrder } from "./types";

export const ProgramFormBackButton = ({
  currentStep,
  isSaving,
  onBack,
}: {
  currentStep: ProgramFormTab;
  isSaving: boolean;
  onBack: () => void;
}) => (
  <Button
    text="Back"
    onClick={onBack}
    disabled={programFormTabOrder.indexOf(currentStep) === 0 || isSaving}
  />
);

/** Next, and Publish. Publish shows only on the last step, Review & publish.
 * Once every step before it is done, an admin who went back to change
 * something gets Review & Publish beside Next, to go straight there instead of
 * pressing Next through each step. Next then steps aside for it. On the step
 * just before the end, Next already goes there, so it stays alone. */
export const ProgramFormForwardButtons = ({
  currentStep,
  canJumpToEnd,
  publishBlockedReason,
  isSaving,
  isPublishing,
  onNext,
  onJumpToEnd,
  onPublish,
}: {
  currentStep: ProgramFormTab;
  /** Every step before the last one is done. */
  canJumpToEnd: boolean;
  /** Set while Publish is not allowed yet, and shown on hover. This is the
   * only Publish button a draft has. */
  publishBlockedReason: string | null;
  isSaving: boolean;
  /** The running save is a publish, so Publish says "Saving". */
  isPublishing: boolean;
  onNext: () => void;
  onJumpToEnd: () => void;
  onPublish: () => void;
}) => {
  const stepsLeft =
    programFormTabOrder.length - 1 - programFormTabOrder.indexOf(currentStep);
  const showJumpToEnd = canJumpToEnd && stepsLeft > 1;
  return (
    <>
      {stepsLeft > 0 && (
        <Button
          text="Next"
          onClick={onNext}
          disabled={isSaving}
          theme={showJumpToEnd ? undefined : buttonTheme.primary}
        />
      )}
      {showJumpToEnd && (
        <Button
          text="Review & Publish"
          onClick={onJumpToEnd}
          disabled={isSaving}
          theme={buttonTheme.primary}
        />
      )}
      {stepsLeft === 0 && (
        <Button
          text={isPublishing ? "Saving" : "Publish"}
          onClick={onPublish}
          disabled={isSaving || !!publishBlockedReason}
          tooltip={publishBlockedReason}
          theme={buttonTheme.primary}
        />
      )}
    </>
  );
};

/** Pinned under the form and holding all of its actions: the step buttons for
 * a draft, Discard and Save for a published review. Its buttons are kept near
 * the middle of the screen rather than in its corners. The width is the same
 * on every tab, the Workflow tab's, so the buttons stay put while the form
 * beneath them changes width. */
const ProgramFormStepBar = ({
  back,
  forward,
}: {
  back: ReactNode;
  forward: ReactNode;
}) => (
  <div className="absolute inset-x-0 bottom-0 z-30 border-t bg-content-background py-3">
    <div
      aria-label="Setup progress"
      className="mx-auto flex max-w-[1248px] items-center justify-between gap-4 px-8"
    >
      {back}
      <div className="flex shrink-0 items-center gap-2">{forward}</div>
    </div>
  </div>
);

export default ProgramFormStepBar;
